import type { ReactNode } from 'react';
import { useContext } from 'react';

import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-badge';
import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkExclamationIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/exclamation';
import BpkExclamationCircleIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/exclamation-circle';
import {
  BpkBox,
  BpkBreakpoint,
  BpkFlex,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import classNames from 'classnames';
import { parseISO } from 'date-fns/parseISO';
import reactReplacePlaceholder from 'react-replace-placeholder';

import AirlineIcon from '../AirlineIcon';
import Codeshare from '../Codeshare';
import LocalisationContext from '../Contexts/LocalisationContext';
import StringsContext from '../Contexts/StringsContext';
import { getTranslatedStatus, STATUS } from '../services/translationService';

import type {
  FlightEntryData,
  FlightStatus,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/ArrivalsDepartures';

import STYLES from './FlightEntry.module.scss';

export interface FlightEntryDataProps extends FlightEntryData {
  isMobile?: boolean;
  showFlightTrackerLinks?: boolean;
  flightTrackerBasePath?: string;
  useUMSFlightTrackerUrls?: boolean;
}

const BADGE_STATUS: Partial<Record<FlightStatus, keyof typeof BADGE_TYPES>> = {
  CANCELLED: BADGE_TYPES.critical,
  DELAYED: BADGE_TYPES.critical,
  NO_TAKEOFF_INFO: BADGE_TYPES.warning,
};

const BADGE_ICONS: Partial<Record<FlightStatus, ReactNode>> = {
  CANCELLED: <BpkExclamationIcon />,
  DELAYED: <BpkExclamationIcon />,
  NO_TAKEOFF_INFO: <BpkExclamationCircleIcon />,
};

const FlightEntry = ({
  airline,
  airlineImage,
  codeshares = undefined,
  departureTime = undefined,
  estimatedTime = undefined,
  flightCode,
  flightTrackerBasePath = '/flight-tracker',
  flightTrackerUrl = undefined,
  gate = undefined,
  id: _id,
  isMobile = false,
  location,
  showFlightTrackerLinks = false,
  status,
  terminal = undefined,
  time = undefined,
  useUMSFlightTrackerUrls = false,
}: FlightEntryDataProps) => {
  const strings = useContext(StringsContext);
  const localisation = useContext(LocalisationContext);
  const badgeType =
    BADGE_STATUS[status as keyof typeof BADGE_STATUS] || BADGE_TYPES.normal;
  const badgeIcon: ReactNode | undefined =
    BADGE_ICONS[status as keyof typeof BADGE_ICONS] || undefined;

  const isDelayedFlight = status === 'DELAYED' && estimatedTime;
  const formatTime = (value: string) =>
    localisation.formatTime?.(parseISO(value.replace('Z', '')));

  const airlineIconEl = <AirlineIcon airlineImage={airlineImage} />;

  // BpkVessel (not BpkBox/BpkFlex): needs `font-size`/`line-height`, which
  // neither BpkBox nor BpkFlex expose as props (verified against
  // commonProps.d.ts — no fontSize/lineHeight/fontWeight prop exists on
  // either). See backpack-api-gaps/WOM-6811-flight-entry-airline-icon.md.
  const airlineEl = airline ? (
    <BpkVessel as="div" className={STYLES.FlightEntry__airline}>
      {airline}
    </BpkVessel>
  ) : undefined;

  // BpkVessel (not BpkBox/BpkFlex): needs `line-height`/`font-size` (same gap
  // as `__airline`), plus the `--linked` variant needs `text-decoration` and
  // `color: var(--bpk-link-color, ...)` — neither exposed as props either.
  const flightCodeEl = (
    <BpkVessel
      as="div"
      className={classNames(STYLES.FlightEntry__flightCode, {
        [STYLES['FlightEntry__flightCode--linked']]: showFlightTrackerLinks,
      })}
    >
      {flightCode}
    </BpkVessel>
  );

  // BpkVessel (not BpkBox/BpkFlex): needs `font-weight`, which neither
  // exposes as a prop.
  const locationEl = (
    <BpkVessel as="div" className={STYLES.FlightEntry__location}>
      {location}
    </BpkVessel>
  );

  const timeAndDelayedTimeEl = (
    // BpkVessel (not BpkBox/BpkFlex): needs `font-weight` (same gap as
    // `__location`). `gap` alone would be expressible, but the whole element
    // has to stay once one property can't move.
    <BpkVessel as="div" className={STYLES.FlightEntry__time}>
      {time && (
        // BpkVessel (not BpkBox/BpkFlex): the `--delayedFlight` variant needs
        // `text-decoration: line-through`, which neither exposes as a prop.
        <BpkVessel
          as="div"
          className={classNames(STYLES.FlightEntry__scheduledTime, {
            [STYLES['FlightEntry__scheduledTime--delayedFlight']]:
              isDelayedFlight,
          })}
        >
          {formatTime(time)}
        </BpkVessel>
      )}
      {isDelayedFlight && (
        // BpkVessel (not BpkText): `color` alone maps to
        // `TEXT_COLORS.textError` (verified: `$bpk-status-danger-spot-day`
        // and `textError` are the same colour, both rgb(231, 8, 102) via
        // `backpack-cli token`) — but `BpkText` imposes its own
        // `font-weight`/`line-height` defaults rather than inheriting them,
        // which breaks this element's reliance on inheriting
        // `font-weight: bold` from its `.time` ancestor. Caught only by the
        // mandatory visual-regression step (1.42% diff at the 375px mobile
        // capture), not by static reasoning — same class of mistake as
        // WOM-6777's Gap 4 note on logical-vs-physical margins.
        <BpkVessel as="span" className={STYLES.FlightEntry__estimatedTime}>
          {formatTime(estimatedTime)}
        </BpkVessel>
      )}
    </BpkVessel>
  );

  // BpkVessel (not BpkBox/BpkFlex): needs `color`/`font-size`/`line-height` —
  // `color` alone would map to `TEXT_COLORS.textSecondary`, but `font-size`/
  // `line-height` have no prop route, so the whole element stays.
  const terminalEl = (
    <BpkVessel as="div" className={STYLES.FlightEntry__terminal}>
      {reactReplacePlaceholder(
        strings.falcon_arrivals_departures_board_flightStatus_terminal_label,
        { terminal: terminal || '-' },
        '{',
        '}',
      )}
    </BpkVessel>
  );

  const gateEl = gate ? (
    // Same gap as `__terminal`.
    <BpkVessel as="div" className={STYLES.FlightEntry__gate}>
      {reactReplacePlaceholder(
        strings.falcon_arrivals_departures_board_flightStatus_gate_label,
        { gate },
        '{',
        '}',
      )}
    </BpkVessel>
  ) : undefined;

  // BpkVessel (not BpkBox/BpkFlex): needs `font-size` and a physical
  // `text-align: right` with no RTL override (the status column is always
  // right-aligned regardless of locale) — `textAlign="end"` would flip to
  // the left in RTL, changing current behaviour (same gap family as
  // WOM-6777's InventoryCard.hotelPrice).
  const statusEl = (
    <BpkVessel as="div" className={STYLES.FlightEntry__status}>
      {status === 'NO_TAKEOFF_INFO' || status === 'UNKNOWN' ? (
        strings[STATUS[status]]
      ) : (
        <BpkBadge type={badgeType}>
          {badgeIcon}
          {getTranslatedStatus(status, strings)}
        </BpkBadge>
      )}
    </BpkVessel>
  );

  const codeSharesEl = codeshares ? (
    <BpkBox
      marginTop={{
        base: BpkSpacing.None,
        [BpkBreakpoint.SmallTablet]: BpkSpacing.SM,
      }}
      marginStart={{
        base: BpkSpacing.None,
        [BpkBreakpoint.SmallTablet]: BpkSpacing.XL,
      }}
    >
      <Codeshare codeshares={codeshares} />
    </BpkBox>
  ) : undefined;

  const cardContents = isMobile ? (
    <BpkFlex direction="column" gap={BpkSpacing.SM}>
      <BpkFlex align="center">
        {airlineIconEl}
        {airlineEl}
        {flightCodeEl}
        <BpkFlex
          direction="column"
          align="flex-end"
          marginStart={BpkSpacing.MD}
        >
          {terminalEl}
          {gateEl}
        </BpkFlex>
      </BpkFlex>
      <BpkBox marginStart={BpkSpacing.XL}>
        {timeAndDelayedTimeEl}
        <BpkFlex justify="space-between" align="center">
          {locationEl}
          {statusEl}
        </BpkFlex>
      </BpkBox>
      {codeSharesEl && (
        <BpkBox marginStart={BpkSpacing.XL}>{codeSharesEl}</BpkBox>
      )}
    </BpkFlex>
  ) : (
    <BpkBox>
      <BpkFlex minHeight="2.5rem" align="center" gap={BpkSpacing.MD}>
        <BpkFlex align="center" minWidth="12rem" basis="16%">
          {airlineIconEl}
          {airlineEl}
        </BpkFlex>
        {flightCodeEl}
        <BpkBox minWidth="6rem" flexBasis="10%">
          {timeAndDelayedTimeEl}
        </BpkBox>
        {locationEl}
        {statusEl}
        <BpkFlex
          direction="column"
          align="flex-end"
          minWidth="6rem"
          basis="13%"
        >
          {terminalEl}
          {gateEl}
        </BpkFlex>
      </BpkFlex>
      {codeSharesEl}
    </BpkBox>
  );

  const departureSlug = departureTime
    ? `#${departureTime.slice(0, 10)}_${departureTime.slice(11, 16).replace(':', '-')}`
    : '';
  const clientFlightTrackerHref = `${flightTrackerBasePath}/${encodeURIComponent(flightCode.toLowerCase())}${departureSlug}`;
  // Only trust an upstream URL that is a same-origin relative path or an
  // absolute https URL; reject javascript:, data:, protocol-relative, etc.
  const isSafeFlightTrackerUrl = (url: string) =>
    (url.startsWith('/') && !url.startsWith('//')) || /^https:\/\//i.test(url);
  const useUpstreamUrl =
    useUMSFlightTrackerUrls &&
    !!flightTrackerUrl &&
    isSafeFlightTrackerUrl(flightTrackerUrl);

  let flightTrackerHref;
  if (showFlightTrackerLinks) {
    flightTrackerHref = useUpstreamUrl
      ? `${flightTrackerUrl}${departureSlug}`
      : clientFlightTrackerHref;
  }

  return (
    <BpkCard
      className={STYLES.FlightEntry}
      data-testid="flight-entry"
      href={flightTrackerHref}
      atomic={showFlightTrackerLinks ? undefined : false}
    >
      {cardContents}
    </BpkCard>
  );
};

export default FlightEntry;
